import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { REPORT_CATEGORIES } from '@scale/frontend-lib/constants';
import SkipOrReportTaskModal from '../SkipOrReportTaskModal';
import { ReviewReportButton } from './ReviewReportButton';
import ReportSensitiveContentTaskModal from './ReportSensitiveContentTaskModal';
import {
  SENSITIVE_CONTENT_REPORT_CATEGORIES,
  HIGH_RISK_SENSITIVE_CONTENT_REPORT_CATEGORIES,
  BENCHMARK_REPORTING_REASONS,
  SENSITIVE_CONTENT_REPORT_TOOLTIPS,
  BULBA_SENSITIVE_REPORT_CATEGORIES,
} from '@scale/shared/defaults';

const DEFAULT_HEADER_TEXT = 'Report this task for sensitive content';
const DEFAULT_BODY_TEXT =
  "Please let us know what's wrong with this task so we can make tasking better for you. If you see an issue that isn't related to sensitive content, contact the project support team for assistance.";
const DEFAULT_CONFIRM_TEXT = 'Report Task';
const DEFAULT_PLACEHOLDER_TEXT = "I'm reporting this task for sensitive content because...";

interface ReportTaskProps {
  handleReport?: (reason: string, explanation: string) => void;
  buttonText?: string;
  style?: React.CSSProperties;
  confirmButtonText?: string;
  reportBodyText?: string;
  reportHeaderText?: string;
  explanationPlaceholder?: string;
  disabled?: boolean;
  reportReasons?: {
    [key: string]: { [key: string]: string };
  };
  reportCategories?: { [key: string]: string };
  closeModalOnReport?: boolean;
  enableSensitiveContentReporting?: boolean;
  enableHighRiskSensitiveContentReporting?: boolean;
  useBulbaSensitiveContentReporting?: boolean;
  isBenchmarkReporting?: boolean;
  avoidButtonRendering?: boolean;
}

export interface ReportButtonProps {
  onClick: () => void;
  hasMargin?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  buttonText?: string;
}

export type ReportTaskRef = {
  openModal: () => void;
  closeModal: () => void;
  isModalOpen: boolean;
  disabled?: boolean;
};

const ReportTask = forwardRef<ReportTaskRef, ReportTaskProps>((props, ref) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  useImperativeHandle(ref, () => {
    return {
      openModal,
      closeModal,
      isModalOpen,
      disabled: props.disabled,
    };
  });

  const handleSubmit = (reason: string, explanation: string) => {
    props.handleReport?.(reason, explanation);
    if (props.closeModalOnReport) {
      closeModal();
    }
  };

  let sensitiveContentReportCategories: { [key: string]: string } = {};
  if (props.useBulbaSensitiveContentReporting) {
    sensitiveContentReportCategories = BULBA_SENSITIVE_REPORT_CATEGORIES;
  } else {
    if (props.enableHighRiskSensitiveContentReporting) {
      sensitiveContentReportCategories = HIGH_RISK_SENSITIVE_CONTENT_REPORT_CATEGORIES;
    }
    if (props.enableSensitiveContentReporting) {
      sensitiveContentReportCategories = {
        ...sensitiveContentReportCategories,
        ...SENSITIVE_CONTENT_REPORT_CATEGORIES,
      };
    }
  }

  return (
    <div>
      {!props.avoidButtonRendering && (
        <ReviewReportButton
          onClick={openModal}
          disabled={props.disabled}
          style={props.style}
          buttonText={props.buttonText}
        />
      )}

      {props.isBenchmarkReporting && (
        <SkipOrReportTaskModal
          handleSubmit={handleSubmit}
          isModalOpen={isModalOpen}
          onCloseModal={closeModal}
          reasons={BENCHMARK_REPORTING_REASONS}
          categories={REPORT_CATEGORIES}
          headerText={props.reportHeaderText || DEFAULT_HEADER_TEXT}
          bodyText={props.reportBodyText || DEFAULT_BODY_TEXT}
          confirmButtonText={props.confirmButtonText || DEFAULT_CONFIRM_TEXT}
          explanationPlaceholder={props.explanationPlaceholder || DEFAULT_PLACEHOLDER_TEXT}
        />
      )}

      {!props.isBenchmarkReporting && (
        <ReportSensitiveContentTaskModal
          handleSubmit={handleSubmit}
          isModalOpen={isModalOpen}
          onCloseModal={closeModal}
          categories={sensitiveContentReportCategories}
          tooltips={SENSITIVE_CONTENT_REPORT_TOOLTIPS}
          confirmButtonText={props.confirmButtonText || DEFAULT_CONFIRM_TEXT}
          bodyText={props.reportBodyText || DEFAULT_BODY_TEXT}
          headerText={props.reportHeaderText || DEFAULT_HEADER_TEXT}
          explanationPlaceholder={props.explanationPlaceholder || DEFAULT_PLACEHOLDER_TEXT}
        />
      )}
    </div>
  );
});

ReportTask.displayName = 'ReportTask';

export default ReportTask;
